// Decoy notes — the cover content shown in the fake folder list.
//
// Each decoy row optionally carries a magician-set datetime stamp. When
// `time` is empty/null the row uses the auto-fallback labels in
// DecoyNotes (Today / Yesterday / weekday / full date based on row
// index). When set, the stored value is used verbatim everywhere the
// row's timestamp is shown.

import { STORAGE } from "./storage";

export const DEFAULT_DECOYS = [
  {
    id: "groceries",
    title: "Groceries",
    preview: "Milk, eggs, sourdough, olive oil…",
    time: "",
    body:
      "Milk (semi-skim)\n" +
      "Eggs — free-range dozen\n" +
      "Sourdough loaf\n" +
      "Olive oil\n" +
      "Tomatoes on the vine\n" +
      "Parmesan\n" +
      "Basil\n" +
      "Pasta (linguine)\n" +
      "\n" +
      "don't forget the wine",
  },
  {
    id: "birthday",
    title: "Birthday gift ideas",
    preview: "Mom's birthday Saturday — maybe that book she mentioned?",
    time: "",
    body:
      "Mom's birthday — Saturday\n" +
      "\n" +
      "Ideas:\n" +
      "- The Salt Path (that book she kept mentioning)\n" +
      "- Le Creuset set she was eyeing\n" +
      "- Weekend at the lakes — check availability\n" +
      "- Framed photo from last summer\n" +
      "\n" +
      "Card from all three of us. Ask dad about the dinner reservation.",
  },
  {
    id: "books",
    title: "Books to read",
    preview: "Borges, Calvino, the one Sarah recommended",
    time: "",
    body:
      "- Labyrinths — Borges\n" +
      "- If on a Winter's Night a Traveler — Calvino\n" +
      "- The Overstory (Sarah)\n" +
      "- Educated — Tara Westover\n" +
      "- Klara and the Sun\n" +
      "- Piranesi\n" +
      "\n" +
      "Check the secondhand shop on Prinsengracht first",
  },
  {
    id: "workout",
    title: "Workout plan",
    preview: "Mon / Wed / Fri — push, pull, legs",
    time: "",
    body:
      "PUSH (Mon)\n" +
      "- Bench press 4x8\n" +
      "- OHP 3x10\n" +
      "- Incline DB press 3x10\n" +
      "- Triceps rope 3x12\n" +
      "\n" +
      "PULL (Wed)\n" +
      "- Deadlift 4x5\n" +
      "- Pull-ups 4x max\n" +
      "- Barbell row 3x8\n" +
      "- Curls 3x12\n" +
      "\n" +
      "LEGS (Fri)\n" +
      "- Squat 5x5\n" +
      "- Romanian DL 3x8\n" +
      "- Leg press 3x10\n" +
      "- Calves 4x15",
  },
  {
    id: "wifi",
    title: "Wifi",
    preview: "Cafe Luca: cafelovers2024",
    time: "",
    body:
      "Cafe Luca — cafelovers2024\n" +
      "Airbnb Amsterdam — thefloorupstairs\n" +
      "Home guest — welcome_2024\n" +
      "Gym — freeguest\n" +
      "Mom's place — SunshineHill22\n" +
      "\n" +
      "Router password taped under desk drawer",
  },
  {
    id: "packing",
    title: "Packing list",
    preview: "Passport, charger, the grey jacket",
    time: "",
    body:
      "- Passport\n" +
      "- Phone charger + adapter\n" +
      "- The grey jacket\n" +
      "- Sneakers + one nicer pair\n" +
      "- Socks x5\n" +
      "- Underwear x5\n" +
      "- Toothbrush + paste\n" +
      "- Sunglasses\n" +
      "- Book (finish Piranesi)\n" +
      "- Headphones\n" +
      "\n" +
      "Check-in opens 24h before — set an alarm",
  },
];

/** Create a short, collision-safe id for newly-added decoys. */
function makeDecoyId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return `decoy-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

/** Preset-id → default body lookup so legacy persisted decoys (that
 *  were saved before we shipped default bodies) can still show
 *  realistic content when opened. Only used when the persisted row
 *  has an empty body AND its id matches one of our shipped presets. */
const DEFAULT_BODY_BY_ID = DEFAULT_DECOYS.reduce((acc, d) => {
  acc[d.id] = d.body || "";
  return acc;
}, {});

/** Load saved decoys or fall back to defaults. */
export function loadDecoys() {
  try {
    const raw = localStorage.getItem(STORAGE.DECOY_NOTES);
    if (!raw) return DEFAULT_DECOYS;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed) || !parsed.length) return DEFAULT_DECOYS;
    return parsed
      .filter((n) => n && typeof n.title === "string")
      .map((n) => {
        const storedBody = typeof n.body === "string" ? n.body : "";
        // Back-fill preset default body when persisted row was saved
        // before we shipped bodies. Magician-typed content still wins.
        const bodyWithFallback =
          storedBody === "" && DEFAULT_BODY_BY_ID[n.id]
            ? DEFAULT_BODY_BY_ID[n.id]
            : storedBody;
        return {
          id: n.id || makeDecoyId(),
          title: n.title,
          preview: n.preview || "",
          // Treat any non-string as auto so legacy persisted rows
          // (missing the `time` field) transparently fall back to the
          // auto labels.
          time: typeof n.time === "string" ? n.time : "",
          // Sticky per-note body: persisted across navigation so
          // reopening the same note shows whatever the spectator /
          // magician last typed until they overwrite it.
          body: bodyWithFallback,
          // Marks rows the spectator/magician created via the (+)
          // compose button. Used by DecoyNotes to gate
          // swipe-to-delete behaviour — preset/magician-curated
          // decoys stay swipe-locked so a casual spectator can't
          // wipe the routine.
          userCreated: n.userCreated === true,
          // Pinned rows float to the top of the Notes list in a
          // dedicated "Pinned" section — mirrors real iOS Notes.
          pinned: n.pinned === true,
        };
      });
  } catch {
    return DEFAULT_DECOYS;
  }
}

export function saveDecoys(arr) {
  try {
    localStorage.setItem(STORAGE.DECOY_NOTES, JSON.stringify(arr || []));
  } catch (e) {
    console.warn("Failed to persist decoys", e);
  }
}

export function newDecoy() {
  return { id: makeDecoyId(), title: "", preview: "", time: "", body: "" };
}

/**
 * Patch a single decoy by id, returning a new array. Used by the
 * trick view to write back the magician's most-recent title/body so
 * the next open of the same row remembers it (sticky inputs).
 * No-op when the id isn't found.
 */
export function updateDecoyById(arr, id, patch) {
  if (!id || !Array.isArray(arr)) return arr;
  let touched = false;
  const next = arr.map((n) => {
    if (n.id !== id) return n;
    touched = true;
    return { ...n, ...patch };
  });
  return touched ? next : arr;
}

// ─── Recently Deleted (soft-delete trash bin) ───────────────────────────
// iOS Notes moves swipe-deleted notes into a "Recently Deleted" folder
// where they persist for ~30 days before being purged forever. We
// mirror that so a spectator poking around in the folder list finds
// the exact same navigation they'd expect on a real device.
//
// Trash entries carry an extra `deletedAt` epoch-ms timestamp used to
// compute the "N days left" label + drive the auto-purge.
export const TRASH_LIFETIME_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

// A tiny seed pool of pre-deleted rows shown the first time a
// spectator opens Recently Deleted. Without this the folder looks
// suspiciously empty on a fresh install. Timestamps are backfilled to
// staggered ages so they read as a natural history of trash.
const DEFAULT_TRASH_SEED = [
  { id: "trash-seed-1", title: "Old shopping list",   preview: "Bread, tomatoes, coffee, that hot sauce", body: "",  daysAgo: 3 },
  { id: "trash-seed-2", title: "Meeting notes 12/4",  preview: "Discussed Q1 roadmap — follow up with Ben", body: "", daysAgo: 8 },
  { id: "trash-seed-3", title: "Recipe — carbonara",  preview: "Guanciale, pecorino, egg yolks, black pepper", body: "", daysAgo: 15 },
  { id: "trash-seed-4", title: "Draft email",         preview: "Hey — thanks for the intro last week. I've been…", body: "", daysAgo: 22 },
];

/** Read the trash from localStorage. Runs auto-purge on read so stale
 *  entries never linger past their 30-day lifetime. Seeds the default
 *  pool on very first access so the folder looks lived-in. */
export function loadTrash() {
  try {
    const raw = localStorage.getItem(STORAGE.DECOY_TRASH);
    if (raw === null) {
      // First-ever access: seed a small realistic trash history.
      const now = Date.now();
      const seeded = DEFAULT_TRASH_SEED.map((s) => ({
        id: s.id,
        title: s.title,
        preview: s.preview,
        body: s.body || "",
        time: "",
        userCreated: false,
        deletedAt: now - (s.daysAgo || 1) * 24 * 60 * 60 * 1000,
      }));
      saveTrash(seeded);
      return seeded;
    }
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    const now = Date.now();
    const alive = parsed
      .filter((n) => n && typeof n.title === "string" && typeof n.deletedAt === "number")
      .filter((n) => now - n.deletedAt < TRASH_LIFETIME_MS)
      .map((n) => ({
        id: n.id || `trash-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        title: n.title,
        preview: n.preview || "",
        body: typeof n.body === "string" ? n.body : "",
        time: typeof n.time === "string" ? n.time : "",
        userCreated: n.userCreated === true,
        deletedAt: n.deletedAt,
      }))
      // Newest deletions on top, matching iOS Notes.
      .sort((a, b) => b.deletedAt - a.deletedAt);
    // Persist the auto-purged result so the next read is faster and
    // the storage doesn't grow unbounded.
    if (alive.length !== parsed.length) saveTrash(alive);
    return alive;
  } catch {
    return [];
  }
}

export function saveTrash(arr) {
  try {
    localStorage.setItem(STORAGE.DECOY_TRASH, JSON.stringify(arr || []));
  } catch (e) {
    console.warn("Failed to persist trash", e);
  }
}

/** Push a note onto the trash with `deletedAt = now`. Idempotent —
 *  if the same id already exists in trash it refreshes the timestamp
 *  so the 30-day countdown restarts. */
export function pushToTrash(note) {
  if (!note || !note.id) return;
  const list = loadTrash();
  const filtered = list.filter((n) => n.id !== note.id);
  filtered.unshift({
    id: note.id,
    title: note.title || "",
    preview: note.preview || "",
    body: note.body || "",
    time: note.time || "",
    userCreated: note.userCreated === true,
    deletedAt: Date.now(),
  });
  saveTrash(filtered);
}

/** Remove a single trash entry by id (used by "Delete permanently"). */
export function removeFromTrash(id) {
  if (!id) return;
  const list = loadTrash();
  const next = list.filter((n) => n.id !== id);
  saveTrash(next);
}

/** Empty the entire trash. Used by the "Empty" button. */
export function emptyTrash() {
  saveTrash([]);
}

/** Pop a trash entry and return it so the caller can re-insert it
 *  into the main decoy list ("Recover"). Returns null if not found. */
export function popFromTrash(id) {
  if (!id) return null;
  const list = loadTrash();
  const found = list.find((n) => n.id === id);
  if (!found) return null;
  saveTrash(list.filter((n) => n.id !== id));
  // Strip trash-only metadata before handing back to the caller.
  const { deletedAt: _da, ...rest } = found;
  return rest;
}

/** How many days remain before the entry is auto-purged. Matches the
 *  "N days left" copy shown in real iOS Notes. Rounded UP so a note
 *  deleted 5 seconds ago still says "30 days" instead of "29". */
export function daysLeftInTrash(deletedAt) {
  if (typeof deletedAt !== "number") return 0;
  const elapsed = Date.now() - deletedAt;
  const remaining = TRASH_LIFETIME_MS - elapsed;
  if (remaining <= 0) return 0;
  return Math.max(1, Math.ceil(remaining / (24 * 60 * 60 * 1000)));
}

// ─── Custom folders (spectator-created, purely decorative) ──────────────
// Real iOS Notes lets the user create their own folders. If a curious
// spectator taps "New Folder" and gets no dialog, the disguise breaks.
// So we honour the affordance: they can create folders (persisted in
// localStorage) that show up in the Folders overview and open into an
// empty notes list. The magician can wipe them from Settings if the
// list gets cluttered between performances.

export function loadCustomFolders() {
  try {
    const raw = localStorage.getItem(STORAGE.DECOY_FOLDERS);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((f) => f && typeof f.name === "string" && f.name.length > 0)
      .map((f) => ({
        id: f.id || `folder-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        name: f.name.slice(0, 60), // cap length to avoid pathological UIs
      }));
  } catch {
    return [];
  }
}

export function saveCustomFolders(arr) {
  try {
    localStorage.setItem(STORAGE.DECOY_FOLDERS, JSON.stringify(arr || []));
  } catch (e) {
    console.warn("Failed to persist custom folders", e);
  }
}

/** Append a new folder with a fresh id, returning the updated list. */
export function addCustomFolder(name) {
  const trimmed = (name || "").trim();
  if (!trimmed) return loadCustomFolders();
  const list = loadCustomFolders();
  const folder = {
    id: `folder-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    name: trimmed.slice(0, 60),
  };
  const next = [...list, folder];
  saveCustomFolders(next);
  return next;
}

/** Remove a folder by id. Returns the updated list. */
export function removeCustomFolder(id) {
  if (!id) return loadCustomFolders();
  const next = loadCustomFolders().filter((f) => f.id !== id);
  saveCustomFolders(next);
  return next;
}

/** Look up a folder by id (used by /folders/:id to display its name). */
export function findCustomFolder(id) {
  return loadCustomFolders().find((f) => f.id === id) || null;
}
